import { actionCableUrl } from "@/utils/meta";
import { type Transport } from "@anycable/core";
import { createCable } from "@anycable/web";
import * as Sentry from "@sentry/vue";

// Mutator client for the generated cable services, mirroring internalAxiosClient
// for the Orval REST client: the single place that configures the transport, so
// the generated code depends only on this file's `getCable` export.
//
// AnyCable's client speaks the vanilla ActionCable protocol
// (actioncable-v1-json), so this works against the existing Rails ActionCable
// server with no backend changes.

type Cable = ReturnType<typeof createCable>;

let cable: Cable | null = null;

// Rails' `action_cable_meta_tag` renders a root-relative path (`/cable`). The
// WebSocket constructor resolves that against the document, but
// `Transport#setParam` runs it through `new URL()`, which throws on a relative
// path — so the identity below could never be applied. AnyCable absolutizes
// only the URL it derives from the DOM itself, never one passed to
// `createCable`, so we do the same conversion here.
const absoluteCableUrl = (): string => {
  const url = actionCableUrl();

  if (/^wss?:\/\//.test(url)) return url;

  return `${window.location.protocol.replace("http", "ws")}//${window.location.host}${url}`;
};

// Identity is resolved here rather than baked into the URL at construction
// time. A public page subscribes from its `<script setup>`, which runs before
// `<WithVisitor>`'s `onMounted` has created the visitor, so a URL fixed at
// construction carries no `visitor_token` and the server rejects the whole
// connection for the tab's lifetime (#11539). AnyCable awaits this configurator
// before every `transport.open()`, so waiting here delays the handshake until an
// identity exists and re-applies it on each reconnect.
const configureIdentity = async (transport: Transport): Promise<void> => {
  try {
    await ensureVisitorSession();
  } catch (error: unknown) {
    // Connect anyway: the server decides. A visitor that could not be created
    // is a rejected connection either way, and throwing here would leave the
    // cable stuck in `connecting` instead of surfacing a close.
    Sentry.captureException(error, {
      tags: { component: "internalCableClient", phase: "identity" },
    });
  }

  const visitorToken = useUserStore().visitorToken;

  if (visitorToken) transport.setParam("visitor_token", visitorToken);
};

/** Lazily create the singleton AnyCable client used by the generated cable services. */
export function getCable(): Cable {
  if (cable) return cable;

  cable = createCable(absoluteCableUrl(), {
    transportConfigurator: configureIdentity,
  });

  return cable;
}
